import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Clock, Trophy } from "lucide-react";
import { calculateTimeInvested, calculateTotalRoadmapTime } from "@/lib/timeHelpers";
import { RoadmapWeek } from "@/lib/roadmapHelpers";

interface TimeTrackerProps {
  roadmap: RoadmapWeek[];
  completedTasks: { [key: string]: boolean };
  className?: string;
}

export const TimeTracker = ({
  roadmap,
  completedTasks,
  className,
}: TimeTrackerProps) => {
  const timeData = useMemo(() => {
    const invested = calculateTimeInvested(completedTasks, roadmap);
    const total = calculateTotalRoadmapTime(roadmap);
    const percentage = Math.round((invested.totalMinutes / total.totalMinutes) * 100);

    return {
      invested,
      total,
      percentage,
    };
  }, [roadmap, completedTasks]);

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          <CardTitle className="text-lg">Time Investment</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Time Invested */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Time Invested</span>
            <span className="text-2xl font-bold">{timeData.invested.formattedTime}</span>
          </div>
          <Progress value={timeData.percentage} className="h-2" />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{timeData.invested.totalHours} hours completed</span>
            <span>{timeData.total.totalHours} hours total</span>
          </div>
        </div>

        {/* Milestones */}
        <div className="pt-4 border-t space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <span>Time Milestones</span>
          </div>

          {[
            { hours: 10, label: "10 Hours" },
            { hours: 25, label: "25 Hours" },
            { hours: 50, label: "50 Hours" },
          ].map((milestone) => {
            const achieved = timeData.invested.totalHours >= milestone.hours;
            return (
              <div
                key={milestone.hours}
                className={cn(
                  "flex items-center justify-between p-2 rounded-lg border",
                  achieved
                    ? "bg-yellow-500/10 border-yellow-500/50"
                    : "bg-muted/20 border-border"
                )}
              >
                <span className="text-sm">{milestone.label}</span>
                {achieved ? (
                  <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400">
                    ✓ Achieved
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {milestone.hours - timeData.invested.totalHours}h to go
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground text-center">
            You've invested <strong>{timeData.invested.formattedTime}</strong> in your learning
            journey. Keep going! 🚀
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(" ");
}
